import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { LANGUAGES, STORAGE_KEY, supported } from '../i18n';
import { 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography, 
  useTheme 
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';

const LanguageSelector = ({ mobile = false }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const urlLang = supported(params.get('lang'));

  // A ?lang link (or back/forward) wins over the saved choice.
  useEffect(() => {
    if (urlLang && urlLang !== i18n.language) i18n.changeLanguage(urlLang);
  }, [urlLang, i18n]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (langCode) => {
    handleMenuClose();
    try { localStorage.setItem(STORAGE_KEY, langCode); } catch (e) { /* storage blocked */ }
    i18n.changeLanguage(langCode);
    // Keep the language in the URL so the page can be shared and indexed per language.
    const next = new URLSearchParams(params);
    if (langCode === 'en') next.delete('lang'); else next.set('lang', langCode);
    setParams(next, { replace: true });
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(mobile
          ? {}
          : {
              position: 'fixed',
              bottom: '80px',
              right: '20px',
              zIndex: 1000,
              backgroundColor: theme.palette.background.paper,
              boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
              p: 0.5,
              borderRadius: '50%',
            }),
      }}
    >
      <IconButton
        onClick={handleMenuOpen}
        color={mobile ? 'inherit' : 'primary'}
        aria-label={t('language.select')}
        aria-controls="language-menu"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <LanguageIcon />
      </IconButton>
      
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-label': t('language.select'),
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            maxHeight: 300,
            minWidth: 180,
          }
        }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem 
            key={lang.code} 
            onClick={() => handleLanguageSelect(lang.code)}
            selected={currentLanguage === lang.code}
            sx={{ 
              justifyContent: 'space-between',
              direction: lang.rtl ? 'rtl' : 'ltr'
            }}
          >
            <Typography variant="body2">{lang.name}</Typography>
            {currentLanguage === lang.code && (
              <CheckIcon fontSize="small" sx={{ marginInlineStart: '8px' }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSelector;